import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'motion/react';
import { useIsMobile } from '../../lib/useIsMobile.js';
import { cn } from '../../lib/utils.js';

/**
 * A sheet that slides up from the bottom edge on a phone — the native pattern for a message's
 * actions, reachable with a thumb — and is a centered card on desktop. Radix owns focus and
 * dismissal (tap outside, Escape); motion owns the slide, as in components/ui/Dialog.tsx.
 */
export function BottomSheet({
  open,
  onOpenChange,
  title,
  children,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Announced by screen readers; callers draw their own visible heading if they want one. */
  title: string;
  children: ReactNode;
  className?: string;
}) {
  const isMobile = useIsMobile();
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-40 bg-black/40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount aria-describedby={undefined}>
              <motion.div
                className={cn(
                  'fixed z-40 flex flex-col overflow-hidden border border-border bg-surface shadow-lg outline-none',
                  isMobile
                    ? 'inset-x-0 bottom-0 max-h-[85dvh] rounded-t-2xl pb-[env(safe-area-inset-bottom)]'
                    : 'left-1/2 top-1/2 max-h-[80vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-card',
                  className,
                )}
                initial={isMobile ? { y: '100%' } : { opacity: 0, scale: 0.96 }}
                animate={isMobile ? { y: 0 } : { opacity: 1, scale: 1 }}
                exit={isMobile ? { y: '100%' } : { opacity: 0, scale: 0.96 }}
                transition={
                  isMobile
                    ? { type: 'spring', stiffness: 420, damping: 40 }
                    : { duration: 0.16, ease: 'easeOut' }
                }
                drag={isMobile ? 'y' : false}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.6 }}
                onDragEnd={(_, info) => {
                  if (info.offset.y > 80 || info.velocity.y > 500) onOpenChange(false);
                }}
              >
                <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
                {isMobile && (
                  <div className="flex justify-center pb-1 pt-2.5" aria-hidden>
                    <span className="h-1 w-10 rounded-full bg-border" />
                  </div>
                )}
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}

/** One full-width, thumb-sized row in a sheet. */
export function SheetAction({
  icon: Icon,
  label,
  onSelect,
  destructive = false,
}: {
  icon: LucideIcon;
  label: string;
  onSelect: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex h-12 w-full items-center gap-3 px-5 text-left text-[15px] hover:bg-surface-2 active:bg-surface-2 md:h-10 md:text-sm',
        destructive ? 'text-danger' : 'text-text',
      )}
    >
      <Icon className="h-[18px] w-[18px] shrink-0" />
      {label}
    </button>
  );
}
